import React, { useEffect, useState } from "react";
import axios from "axios";
import AnimatedSystemMap from "../components/AnimatedSystemMap";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Public marketing showcard for RAW Data.
 * Route: /showcard   (clean, brandable, no query params)
 *
 * Sections (in owner-approved order):
 *   1. Hero — gold-on-black logo + hook + £49.99 CTA
 *   2. Features
 *   3. Hardware / Equipment needed
 *   4. What you'll need (bring-your-own API keys)
 *   5. Big CTA + trust bar
 */
export default function Showcard() {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [email, setEmail] = useState("");
  const [allAccess, setAllAccess] = useState(null);   // null | {all_access, portal_url}
  const [checking, setChecking] = useState(false);

  // Handle Stripe redirect back:
  //   /payment/success?session_id=cs_xxx  → poll /api/payments/status until paid
  //   /payment/cancel                     → show cancelled banner
  //   /showcard?paid=1|cancelled=1        → legacy flag support
  const params = new URLSearchParams(window.location.search);
  const path = window.location.pathname;
  const sessionId = params.get("session_id");
  const isCancel = path.endsWith("/cancel") || params.get("cancelled") === "1";
  const [paidResolved, setPaidResolved] = useState(params.get("paid") === "1");
  const [polling, setPolling] = useState(false);

  useEffect(() => {
    if (!sessionId || !path.endsWith("/success") || paidResolved) return;
    let alive = true;
    let attempts = 0;
    setPolling(true);
    const tick = async () => {
      if (!alive) return;
      attempts += 1;
      try {
        const r = await axios.get(`${API}/payments/status/${sessionId}`);
        if (r.data?.payment_status === "paid") {
          setPaidResolved(true);
          setPolling(false);
          return;
        }
      } catch { /* ignore */ }
      if (attempts < 20) setTimeout(tick, 2000);
      else setPolling(false);
    };
    tick();
    return () => { alive = false; };
  }, [sessionId, path, paidResolved]);

  const paid = paidResolved;
  const cancelled = isCancel;

  const buy = async () => {
    setBusy(true); setErr("");
    try {
      const r = await axios.post(`${API}/payments/checkout`, {
        lookup_key: "raw_data_lifetime_gbp",
        origin_url: window.location.origin,
        email: email || undefined,
      });
      window.location.href = r.data.checkout_url;
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message || "Payment failed to start");
      setBusy(false);
    }
  };

  // ── RAW All-Access unlock — backend-only check against rawcompanytech.com ──
  // Triggered when the user types their email + tabs / blurs, OR when they
  // hit the buy button. If their email is on the RAW All-Access roster,
  // the paywall vanishes and they get a gold badge instead.
  const checkAllAccess = async (addr) => {
    const emailAddr = (addr || email || "").trim();
    if (!emailAddr.includes("@")) return;
    setChecking(true);
    try {
      const r = await axios.post(`${API}/all_access/check`, { email: emailAddr });
      setAllAccess(r.data);
    } catch { /* ignore — normal paywall */ }
    finally { setChecking(false); }
  };
  useEffect(() => {
    // Also check if a `session_id` returned (post-Stripe) OR ?email=... in URL
    const q = new URLSearchParams(window.location.search).get("email");
    if (q) { setEmail(q); checkAllAccess(q); }
  }, []);

  const unlocked = allAccess?.all_access === true;

  return (
    <div style={{ minHeight: "100vh", background: "#000", color: "#f5f0dc", fontFamily: "'Inter', system-ui, sans-serif" }}>
      {/* Banner if returning from Stripe */}
      {paid && (
        <div style={{ background: "rgba(34,197,94,0.15)", borderBottom: "1px solid #22c55e", padding: "14px 20px", textAlign: "center", color: "#4ade80", fontSize: 14 }}>
          ✓ Payment received. Check your email for the pairing passphrase — you&apos;ll be in within minutes.
        </div>
      )}
      {polling && !paid && (
        <div style={{ background: "rgba(255,184,0,0.12)", borderBottom: "1px solid #ffb800", padding: "14px 20px", textAlign: "center", color: "#ffb800", fontSize: 14 }}>
          Confirming your payment with Stripe…
        </div>
      )}
      {cancelled && (
        <div style={{ background: "rgba(255,177,66,0.12)", borderBottom: "1px solid #ffb800", padding: "14px 20px", textAlign: "center", color: "#ffb800", fontSize: 14 }}>
          Payment cancelled — you&apos;re welcome back any time.
        </div>
      )}

      {/* ─── 1. HERO ─── */}
      <section style={{ maxWidth: 1100, margin: "0 auto", padding: "80px 24px 48px", textAlign: "center" }}>
        <img
          src="/raw-data-logo.png"
          alt="RAW DATA"
          data-testid="showcard-logo"
          style={{ width: 128, height: 128, display: "block", margin: "0 auto 24px", borderRadius: "50%" }}
        />
        <h1 style={{ fontSize: "clamp(36px,6vw,64px)", fontWeight: 800, letterSpacing: "0.02em", color: "#ffb800", margin: 0, textShadow: "0 0 30px rgba(255,184,0,0.35)" }}>
          RAW DATA
        </h1>
        <div style={{ color: "#f5f0dc", opacity: 0.7, letterSpacing: "0.28em", fontSize: 12, marginTop: 8 }}>
          SECURE WEALTH ANALYTICS · VOICE-FIRST COMMAND CENTRE
        </div>
        <p style={{ maxWidth: 780, margin: "36px auto 0", fontSize: "clamp(16px,1.8vw,20px)", lineHeight: 1.6, opacity: 0.92 }}>
          Your secure command centre for money-flow and wealth analytics.
          Track daily briefs, monitor performance, and control your home,
          computer and email — all by voice — from one clean, private
          dashboard. Serious data, simply presented.
        </p>

        <div style={{ marginTop: 44, display: "flex", flexDirection: "column", alignItems: "center", gap: 12 }}>
          <div style={{ fontSize: 12, letterSpacing: "0.3em", opacity: 0.6 }}>LIFETIME ACCESS</div>
          <div style={{ fontSize: 56, fontWeight: 800, color: "#ffb800", lineHeight: 1 }}>£49.99</div>
          <div style={{ fontSize: 12, opacity: 0.55 }}>One-time payment · no subscription · your keys, your data</div>
          <input
            data-testid="showcard-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onBlur={() => checkAllAccess()}
            placeholder="your@email.com (we'll send the passphrase here)"
            style={{ marginTop: 20, width: "min(420px,90vw)", padding: "12px 16px", fontSize: 14, background: "rgba(255,184,0,0.05)", border: "1px solid rgba(255,184,0,0.35)", borderRadius: 6, color: "#f5f0dc", outline: "none" }}
          />
          {unlocked ? (
            <RawAllAccessBadge data={allAccess} onOpen={() => window.location.href = "/"} big />
          ) : (
            <>
              <button
                data-testid="showcard-buy"
                onClick={buy}
                disabled={busy}
                style={{ marginTop: 8, background: "#ffb800", color: "#000", padding: "16px 40px", fontSize: 16, fontWeight: 800, letterSpacing: "0.1em", border: "none", borderRadius: 6, cursor: busy ? "wait" : "pointer", boxShadow: "0 0 30px rgba(255,184,0,0.35)", opacity: busy ? 0.6 : 1 }}
              >{busy ? "OPENING CHECKOUT…" : "GET LIFETIME ACCESS · £49.99"}</button>
              {checking && <div style={{ fontSize: 11, opacity: 0.55, marginTop: 6 }}>Checking RAW All-Access…</div>}
            </>
          )}
          {err && <div style={{ color: "#ef4444", fontSize: 12, marginTop: 8 }}>{err}</div>}
        </div>
      </section>

      {/* ─── 2. FEATURES ─── */}
      <section style={{ maxWidth: 1100, margin: "0 auto", padding: "60px 24px" }}>
        <div style={{ color: "#ffb800", letterSpacing: "0.3em", fontSize: 11, opacity: 0.7, textAlign: "center" }}>WHAT&apos;S INSIDE</div>
        <h2 style={{ textAlign: "center", fontSize: "clamp(28px,4vw,44px)", fontWeight: 700, margin: "12px 0 40px", color: "#ffb800" }}>Everything, spoken.</h2>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(260px,1fr))", gap: 20 }}>
          {[
            {icon: "🔐", title: "Secure Data Node", body: "Enterprise-grade privacy for your numbers. Your keys, your data — the app talks to your accounts, not ours."},
            {icon: "📊", title: "Wealth Analytics", body: "Live charts, monthly P&L, all-time win/loss, and per-app revenue rollups. Every stat, every day."},
            {icon: "📩", title: "Daily Money-Flow Brief", body: "Weather · Capital.com balance · sister-app revenue · calendar · BBC headlines — read aloud in a calm British voice every morning."},
            {icon: "⚡", title: "Live Insights", body: "Live position tickers, autonomous auto-trader, real-time swarm-chat with 5 named agents (Tom · Sarah · Eva · Scout · Dobby)."},
            {icon: "🏠", title: "Home + Computer Control", body: "'Turn on the bedroom TV.' 'Shut my PC down.' 'Show me the doorbell.' Voice commands drive your smart plugs, cameras, and computer."},
            {icon: "📧", title: "Email + Calendar Voice", body: "Read your Gmail. Draft replies with Claude Sonnet 4.5. List today's calendar. Send responses back through your own accounts."},
            {icon: "📮", title: "Agent Inbox", body: "Emails, app improvements, and priority senders all pop up in one inbox — spoken alerts in each agent's own voice."},
            {icon: "🧠", title: "Mind Coach + Conclave", body: "7 personas (Billionaire Thinker · 10× Learning Tutor · Cognitive OS · Time-Leverage Strategist…) plus multi-agent huddles that debate your decisions."},
          ].map((f) => (
            <div key={f.title} style={{ background: "rgba(255,184,0,0.04)", border: "1px solid rgba(255,184,0,0.2)", padding: 22, borderRadius: 8 }}>
              <div style={{ fontSize: 28, marginBottom: 10 }}>{f.icon}</div>
              <div style={{ fontSize: 15, fontWeight: 700, color: "#ffb800", marginBottom: 6 }}>{f.title}</div>
              <div style={{ fontSize: 13, lineHeight: 1.55, opacity: 0.85 }}>{f.body}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ─── 2.5 THE MAIN ORB — screenshot + 2-screen callout ─── */}
      <section style={{ maxWidth: 1200, margin: "0 auto", padding: "60px 24px", borderTop: "1px solid rgba(255,184,0,0.15)" }}>
        <div style={{ color: "#ffb800", letterSpacing: "0.3em", fontSize: 11, opacity: 0.7, textAlign: "center" }}>THE WORKSTATION</div>
        <h2 style={{ textAlign: "center", fontSize: "clamp(28px,4vw,44px)", fontWeight: 700, margin: "12px 0 12px", color: "#ffb800" }}>Two screens. One brain.</h2>
        <p style={{ textAlign: "center", opacity: 0.75, fontSize: 15, maxWidth: 760, margin: "0 auto 40px", lineHeight: 1.6 }}>
          The main <b>Orb screen</b> is your voice cockpit — the pulsing gold orb, the agent halo (Tom · Sarah · Eva · Scout · Dobby), Swarm Chat, live trading arm-switch, and every launcher. Tap
          <span style={{ display: "inline-block", background: "#ffb800", color: "#000", padding: "2px 10px", margin: "0 6px", fontWeight: 800, borderRadius: 4, fontSize: 12 }}>2ND SCREEN</span>
          and the <b>Mirror screen</b> pops out on your second monitor — morning-brief cards, live P&amp;L ticker, calendar, news, and camera feeds — for a full onboard command centre.
        </p>
        <div style={{ position: "relative", background: "#000", border: "1px solid rgba(255,184,0,0.35)", borderRadius: 12, overflow: "hidden", boxShadow: "0 0 80px rgba(255,184,0,0.15)", padding: "40px 24px" }}>
          <OrbMockup />
        </div>
        <div style={{ marginTop: 24, display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(220px,1fr))", gap: 14, fontSize: 12 }}>
          {[
            { title: "🔮 The Orb", body: "Tap or say \"Raw Data\" to wake. Speaks in a calm British voice." },
            { title: "🎯 Agent Halo", body: "Five named agents orbit — call any of them by name." },
            { title: "💬 Swarm Chat", body: "Ask all agents at once · Debate · Huddle for multi-agent consensus." },
            { title: "🖥 2nd Screen", body: "Pop the Mirror out to a second monitor for the full brief." },
            { title: "👁 EYES · Vision", body: "\"What do you see?\" — webcam vision on demand." },
            { title: "⚡ Live Trading", body: "Arm the auto-trader on Capital.com from the bottom-left toggle." },
          ].map(x => (
            <div key={x.title} style={{ padding: 12, background: "rgba(255,184,0,0.03)", border: "1px solid rgba(255,184,0,0.15)", borderRadius: 6 }}>
              <div style={{ fontWeight: 700, color: "#ffb800", marginBottom: 4 }}>{x.title}</div>
              <div style={{ opacity: 0.85, lineHeight: 1.5 }}>{x.body}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ─── 2.7 SYSTEM MAP — the live animated architecture from inside the app ─── */}
      <section style={{ maxWidth: 1200, margin: "0 auto", padding: "60px 24px", borderTop: "1px solid rgba(255,184,0,0.15)" }}>
        <div style={{ color: "#ffb800", letterSpacing: "0.3em", fontSize: 11, opacity: 0.7, textAlign: "center" }}>HOW IT ALL CONNECTS</div>
        <h2 style={{ textAlign: "center", fontSize: "clamp(28px,4vw,44px)", fontWeight: 700, margin: "12px 0 12px", color: "#ffb800" }}>The RAW Data system map</h2>
        <p style={{ textAlign: "center", opacity: 0.75, fontSize: 14, maxWidth: 640, margin: "0 auto 24px", lineHeight: 1.6 }}>
          The exact live map baked into your app — every service, every wire, every data flow. Watch the dots trace between the Orb, agents, Capital.com, Google, Shelly, and everything else.
        </p>
        <div style={{
          background: "#000", border: "1px solid rgba(255,184,0,0.25)", borderRadius: 12,
          boxShadow: "0 0 60px rgba(255,184,0,0.12)",
          aspectRatio: "1200 / 720",   // Match AnimatedSystemMap's own viewBox
          width: "100%",
          maxWidth: 1200,
          margin: "0 auto",
          position: "relative",
          overflow: "visible",         // Do NOT clip — the bottom nodes (Facebook, TikTok, Instagram) sit near y≈700
        }} data-testid="showcard-system-map">
          <AnimatedSystemMap />
        </div>
      </section>

      {/* ─── 2.8 FULL COMMAND LIST ─── */}
      <section style={{ maxWidth: 1200, margin: "0 auto", padding: "60px 24px", borderTop: "1px solid rgba(255,184,0,0.15)" }}>
        <div style={{ color: "#ffb800", letterSpacing: "0.3em", fontSize: 11, opacity: 0.7, textAlign: "center" }}>78+ VOICE COMMANDS</div>
        <h2 style={{ textAlign: "center", fontSize: "clamp(28px,4vw,44px)", fontWeight: 700, margin: "12px 0 12px", color: "#ffb800" }}>Everything you can say</h2>
        <p style={{ textAlign: "center", opacity: 0.7, fontSize: 14, maxWidth: 620, margin: "0 auto 32px" }}>
          The full built-in cheat sheet — grouped exactly like it appears inside the app when you say &quot;what can you do?&quot;.
        </p>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(300px,1fr))", gap: 18 }}>
          {COMMAND_GROUPS.map(g => (
            <div key={g.title} data-testid={`cmd-group-${g.title.replace(/\s+/g,'-')}`}
                 style={{ background: "rgba(255,184,0,0.03)", border: "1px solid rgba(255,184,0,0.18)", borderRadius: 8, padding: 18 }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: "#ffb800", marginBottom: 10 }}>{g.title}</div>
              <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12, opacity: 0.9, lineHeight: 1.65 }}>
                {g.items.slice(0, 7).map((it, i) => <li key={i}>&quot;{it}&quot;</li>)}
                {g.items.length > 7 && <li style={{ opacity: 0.5, listStyle: "none", marginTop: 4 }}>+ {g.items.length - 7} more…</li>}
              </ul>
            </div>
          ))}
        </div>
      </section>

      {/* ─── 3. HARDWARE / EQUIPMENT ─── */}
      <section style={{ maxWidth: 1100, margin: "0 auto", padding: "60px 24px", borderTop: "1px solid rgba(255,184,0,0.15)" }}>
        <div style={{ color: "#ffb800", letterSpacing: "0.3em", fontSize: 11, opacity: 0.7, textAlign: "center" }}>HARDWARE YOU&apos;LL WANT</div>
        <h2 style={{ textAlign: "center", fontSize: "clamp(28px,4vw,44px)", fontWeight: 700, margin: "12px 0 12px", color: "#ffb800" }}>Equipment for the full experience</h2>
        <p style={{ textAlign: "center", opacity: 0.65, fontSize: 14, maxWidth: 640, margin: "0 auto 40px" }}>
          The app works on any modern browser out of the box. Add these
          optional bits of kit to unlock the full voice-first, always-on,
          multi-screen experience.
        </p>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(280px,1fr))", gap: 20 }}>
          {[
            {icon: "🖥", label: "Two monitors", why: "One for the orb, one for the Mirror screen (morning brief cards, launchers, live P&L). Any second screen works — the app auto-detects and pops the Mirror onto it."},
            {icon: "🎤", label: "USB / Bluetooth microphone", why: "The built-in laptop mic works, but a dedicated mic (Blue Yeti, Rode NT-USB, or any earbud mic) gives cleaner wake-word detection and less TTS echo."},
            {icon: "🔊", label: "Speakers or headphones", why: "So Jarvis and the agents can talk back to you. Any powered speakers or a Bluetooth headset works."},
            {icon: "📷", label: "Webcam (built-in OK)", why: "For the 👁 EYES vision feature — 'what's on my screen?' / 'what am I looking at?'. The MacBook / Windows built-in camera is fine."},
            {icon: "🔘", label: "ESP32 wake-word puck (optional)", why: "A physical always-listening button that fires the wake webhook. £8 board + a mic. Setup guide inside the app."},
            {icon: "💡", label: "Shelly smart plugs (optional)", why: "For 'turn on the bedroom TV' style commands. Shelly Plug S is ~£12 each and connects via cloud."},
            {icon: "🚪", label: "Ring doorbell (optional)", why: "So 'show me the doorbell' returns a live snapshot. Any Ring model with cloud access works."},
            {icon: "📡", label: "Home Wi-Fi + a modern browser", why: "Chrome, Edge or Safari on any laptop from the last 5 years. That's it — no downloads, no installs."},
          ].map((h) => (
            <div key={h.label} style={{ background: "rgba(255,184,0,0.03)", border: "1px solid rgba(255,184,0,0.15)", padding: 18, borderRadius: 8 }}>
              <div style={{ fontSize: 22, marginBottom: 8 }}>{h.icon}</div>
              <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 4 }}>{h.label}</div>
              <div style={{ fontSize: 12, lineHeight: 1.55, opacity: 0.75 }}>{h.why}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ─── 4. API KEYS YOU BRING ─── */}
      <section style={{ maxWidth: 1100, margin: "0 auto", padding: "60px 24px", borderTop: "1px solid rgba(255,184,0,0.15)" }}>
        <div style={{ color: "#ffb800", letterSpacing: "0.3em", fontSize: 11, opacity: 0.7, textAlign: "center" }}>YOUR KEYS · YOUR CONTROL</div>
        <h2 style={{ textAlign: "center", fontSize: "clamp(28px,4vw,44px)", fontWeight: 700, margin: "12px 0 12px", color: "#ffb800" }}>Bring your own accounts</h2>
        <p style={{ textAlign: "center", opacity: 0.65, fontSize: 14, maxWidth: 640, margin: "0 auto 40px" }}>
          RAW Data runs on our secure backend, but the integrations use
          <b> your </b>API keys — so it&apos;s <b>your</b> Gmail, <b>your</b>
          Capital.com account, <b>your</b> smart plugs. We never touch
          them. The app ships with a paste-and-go setup wizard.
        </p>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(220px,1fr))", gap: 12, fontSize: 12 }}>
          {[
            ["🧠 Emergent Universal LLM", "profile.emergent.sh"],
            ["🎙 ElevenLabs (voices)", "elevenlabs.io"],
            ["📈 Capital.com (trading)", "capital.com"],
            ["📧 Google (Gmail + Calendar)", "developers.google.com"],
            ["📮 Resend (send email)", "resend.com"],
            ["📣 Meta (Facebook + IG)", "developers.facebook.com"],
            ["🎬 TikTok", "developers.tiktok.com"],
            ["🐦 X / Twitter", "developer.x.com"],
            ["💼 LinkedIn", "linkedin.com/developers"],
            ["📺 YouTube Data API", "console.cloud.google.com"],
            ["🏠 Shelly Cloud (plugs)", "control.shelly.cloud"],
            ["🚪 Ring (doorbell)", "ring.com"],
            ["🐙 GitHub (repos)", "github.com/settings/tokens"],
            ["🤖 xAI Grok (optional)", "console.x.ai"],
          ].map(([label, host]) => (
            <div key={label} style={{ padding: "10px 14px", background: "rgba(255,184,0,0.03)", border: "1px solid rgba(255,184,0,0.12)", borderRadius: 6 }}>
              <div style={{ color: "#f5f0dc" }}>{label}</div>
              <div style={{ color: "#ffb800", opacity: 0.7, fontSize: 11, marginTop: 2 }}>{host}</div>
            </div>
          ))}
        </div>
        <div style={{ textAlign: "center", marginTop: 32, opacity: 0.65, fontSize: 13 }}>
          Any key you don&apos;t have, the feature stays dark. Add it later — the app degrades gracefully.
        </div>
      </section>

      {/* ─── 5. CTA + TRUST ─── */}
      <section style={{ maxWidth: 900, margin: "0 auto", padding: "60px 24px 100px", textAlign: "center" }}>
        <h2 style={{ fontSize: "clamp(30px,4vw,44px)", fontWeight: 800, color: "#ffb800", margin: 0 }}>
          Ready when you are.
        </h2>
        <div style={{ marginTop: 32 }}>
          {unlocked ? (
            <RawAllAccessBadge data={allAccess} onOpen={() => window.location.href = "/"} big />
          ) : (
            <button
              data-testid="showcard-buy-2"
              onClick={buy}
              disabled={busy}
              style={{ background: "#ffb800", color: "#000", padding: "18px 44px", fontSize: 17, fontWeight: 800, letterSpacing: "0.1em", border: "none", borderRadius: 6, cursor: busy ? "wait" : "pointer", boxShadow: "0 0 40px rgba(255,184,0,0.45)", opacity: busy ? 0.6 : 1 }}
            >{busy ? "OPENING CHECKOUT…" : "GET LIFETIME ACCESS · £49.99"}</button>
          )}
        </div>
        <div style={{ marginTop: 40, fontSize: 11, opacity: 0.55, letterSpacing: "0.15em" }}>
          RAW COMPANY TECH LIMITED · UK · SECURE STRIPE CHECKOUT · GBP
        </div>
        <div style={{ marginTop: 12, fontSize: 12, opacity: 0.5 }}>
          <a href="https://raw-data.uk" style={{ color: "#ffb800" }}>raw-data.uk</a> ·
          part of the RAW Company family (SnapDog · Raw Mixes · Clothing · Trader · Racing Form Guide)
        </div>
      </section>
    </div>
  );
}

// ────────── RawAllAccessBadge — gold "✓ RAW All-Access" chip + open CTA ──────────
function RawAllAccessBadge({ data, onOpen, big = false }) {
  const scale = big ? 1 : 0.85;
  return (
    <div data-testid="raw-all-access-badge" style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 12 * scale, marginTop: 8 }}>
      <div style={{
        display: "inline-flex", alignItems: "center", gap: 10,
        padding: `${10 * scale}px ${18 * scale}px`, borderRadius: 999,
        background: "linear-gradient(135deg, #ffd75a 0%, #ffb800 50%, #b07800 100%)",
        color: "#000", fontWeight: 800, fontSize: 13 * scale, letterSpacing: "0.15em",
        boxShadow: "0 0 30px rgba(255,184,0,0.55), inset 0 0 0 1px rgba(255,255,255,0.35)",
      }}>
        <span style={{ fontSize: 16 * scale }}>👑</span>
        RAW ALL-ACCESS ✓
      </div>
      <div style={{ fontSize: 12, color: "#ffb800", opacity: 0.9, textAlign: "center", maxWidth: 420 }}>
        Every RAW Company app is unlocked for you — RAW Data, Racing, SnapDog cellular tracker, and more. No payment needed.
      </div>
      <div style={{ display: "flex", gap: 10, marginTop: 4, flexWrap: "wrap", justifyContent: "center" }}>
        <button
          data-testid="all-access-open"
          onClick={onOpen}
          style={{ background: "#ffb800", color: "#000", padding: "14px 32px", fontSize: 15, fontWeight: 800, letterSpacing: "0.1em", border: "none", borderRadius: 6, cursor: "pointer", boxShadow: "0 0 30px rgba(255,184,0,0.4)" }}
        >OPEN RAW DATA →</button>
        <a
          data-testid="all-access-portal"
          href={data?.portal_url || "https://rawcompanytech.com/members"}
          target="_blank" rel="noopener noreferrer"
          style={{ background: "transparent", color: "#ffb800", padding: "14px 24px", fontSize: 13, fontWeight: 700, letterSpacing: "0.1em", border: "1px solid #ffb800", borderRadius: 6, textDecoration: "none", display: "inline-flex", alignItems: "center" }}
        >MEMBERS PORTAL ↗</a>
      </div>
    </div>
  );
}

// ────────── OrbMockup — inline SVG hero of the main workstation ──────────
// Always matches the showcard aesthetic; no dependency on screenshots.
function OrbMockup() {
  const agents = [
    { name: "TOM",   angle:   0, color: "#22d3ee" },
    { name: "SARAH", angle:  72, color: "#a78bfa" },
    { name: "EVA",   angle: 144, color: "#4ade80" },
    { name: "SCOUT", angle: 216, color: "#f59e0b" },
    { name: "DOBBY", angle: 288, color: "#f472b6" },
  ];
  const CX = 380, CY = 260, RADIUS = 180;
  return (
    <div style={{ position: "relative", width: "100%", maxWidth: 900, margin: "0 auto" }}>
      <svg viewBox="0 0 900 540" width="100%" style={{ display: "block" }}>
        <defs>
          <radialGradient id="orbGrad" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#fff2c0" stopOpacity="1"/>
            <stop offset="55%" stopColor="#ffb800" stopOpacity="0.9"/>
            <stop offset="100%" stopColor="#663d00" stopOpacity="0"/>
          </radialGradient>
          <filter id="glow"><feGaussianBlur stdDeviation="4"/></filter>
        </defs>
        {/* Orbit rings */}
        {[RADIUS - 40, RADIUS, RADIUS + 40].map((r, i) => (
          <circle key={i} cx={CX} cy={CY} r={r}
                  fill="none" stroke="#ffb800"
                  strokeOpacity={0.14 - i * 0.03} strokeDasharray="2 6" />
        ))}
        {/* Central pulsing orb */}
        <circle cx={CX} cy={CY} r="110" fill="url(#orbGrad)">
          <animate attributeName="r" values="105;115;105" dur="4s" repeatCount="indefinite"/>
        </circle>
        <circle cx={CX} cy={CY} r="70" fill="#ffb800" opacity="0.5" filter="url(#glow)"/>
        {/* Orbiting agents */}
        {agents.map((a) => {
          const rad = (a.angle * Math.PI) / 180;
          const x = CX + Math.cos(rad) * RADIUS;
          const y = CY + Math.sin(rad) * RADIUS;
          return (
            <g key={a.name}>
              <circle cx={x} cy={y} r="6" fill={a.color}>
                <animate attributeName="r" values="5;9;5" dur="2.5s" repeatCount="indefinite" begin={`${a.angle * 0.008}s`}/>
              </circle>
              <text x={x} y={y - 14} textAnchor="middle" fill={a.color}
                    fontSize="14" fontWeight="700" fontFamily="'Inter',sans-serif"
                    style={{ letterSpacing: "0.15em" }}>{a.name}</text>
            </g>
          );
        })}
        {/* Brand at bottom */}
        <text x={CX} y="490" textAnchor="middle" fill="#ffb800" fontSize="26" fontWeight="800"
              fontFamily="'Inter',sans-serif" style={{ letterSpacing: "0.3em" }}>RAW-DATA</text>
        <text x={CX} y="512" textAnchor="middle" fill="#ffb800" fontSize="10" opacity="0.55"
              fontFamily="'Inter',sans-serif" style={{ letterSpacing: "0.2em" }}>
          TAP THE ORB TO SPEAK · OR SAY &quot;RAW COMPANY&quot;
        </text>
        {/* Right-hand toggle stack */}
        <g transform="translate(700,140)">
          <rect x="0" y="0" width="120" height="52" rx="6" fill="none"
                stroke="#ffb800" strokeWidth="2">
            <animate attributeName="stroke-opacity" values="0.4;1;0.4" dur="1.6s" repeatCount="indefinite"/>
          </rect>
          <text x="60" y="22" textAnchor="middle" fill="#ffb800" fontSize="10" opacity="0.65"
                fontFamily="'Inter',sans-serif" style={{ letterSpacing: "0.2em" }}>SCREEN 2</text>
          <text x="60" y="38" textAnchor="middle" fill="#ffb800" fontSize="14" fontWeight="700"
                fontFamily="'Inter',sans-serif" style={{ letterSpacing: "0.1em" }}>POP OUT ↗</text>
          <rect x="0" y="64" width="120" height="52" rx="6" fill="rgba(255,184,0,0.06)"
                stroke="rgba(255,184,0,0.35)"/>
          <text x="60" y="86" textAnchor="middle" fill="#ffb800" fontSize="10" opacity="0.6"
                fontFamily="'Inter',sans-serif" style={{ letterSpacing: "0.2em" }}>EYES</text>
          <text x="60" y="102" textAnchor="middle" fill="#ffb800" fontSize="14" fontWeight="700"
                fontFamily="'Inter',sans-serif">👁 VISION</text>
          <rect x="0" y="128" width="120" height="52" rx="6" fill="rgba(255,184,0,0.06)"
                stroke="rgba(255,184,0,0.35)"/>
          <text x="60" y="150" textAnchor="middle" fill="#ffb800" fontSize="10" opacity="0.6"
                fontFamily="'Inter',sans-serif" style={{ letterSpacing: "0.2em" }}>ARM</text>
          <text x="60" y="166" textAnchor="middle" fill="#ffb800" fontSize="14" fontWeight="700"
                fontFamily="'Inter',sans-serif">⚡ LIVE</text>
        </g>
        {/* Callout arrow → 2nd screen chip */}
        <path d="M 640 100 Q 700 90 700 155" fill="none" stroke="#ffb800"
              strokeWidth="1.5" strokeDasharray="3 4" opacity="0.7"/>
        <text x="500" y="90" fill="#ffb800" fontSize="12" fontWeight="700"
              fontFamily="'Inter',sans-serif" style={{ letterSpacing: "0.05em" }}>
          ← Link a 2nd monitor for the full onboard system
        </text>
      </svg>
    </div>
  );
}

// ────────── COMMAND_GROUPS — mirrors the app's built-in show_commands ──────────
const COMMAND_GROUPS = [
  { title: "💬 CONVERSATION", items: ["Raw Company", "How's your day going?", "You alright?", "Good morning Raw Company", "I'm back"] },
  { title: "📈 TRADING", items: ["Place a £500 sell on Bitcoin", "Close my Bitcoin position", "What trades have I got open?", "Move my gold stop to 25 pips", "Arm live trading", "Stop auto-trading (kill switch)", "Switch to mean_reversion strategy"] },
  { title: "🎙 VOICE & AGENTS", items: ["Hey Sarah, tell me a joke", "Hey Eva, what should I post today?", "Hey Dobby, how are the ad numbers?", "Hey Scout, run diagnostics", "Ask all agents about my losing streak", "Debate: should I go live or wait?", "Huddle on the auto-trader"] },
  { title: "📮 AGENT INBOX", items: ["Read my inbox", "Read them out (🔊 Read all)", "AI draft me a reply to that email", "Yes fix it / Dismiss that", "👀 Auto-triage: silence known senders", "🚨 Priority senders: force urgent alarm"] },
  { title: "🤝 CONCLAVE / HUDDLE", items: ["Get my agents to huddle about the losing streak", "Team, work out why my brief keeps skipping", "Agents, work together on my SL/TP strategy", "Show me the last huddle summary"] },
  { title: "⚡ AUTONOMY & LEADERBOARD", items: ["Turn on auto-apply / Turn off auto-apply", "Show me pending autonomous actions", "Show me the agent leaderboard", "Apply the pending action / Dismiss it"] },
  { title: "🧠 MIND COACH", items: ["Open mind coach", "Talk to the Billionaire Thinker", "Talk to the 10x Learning Tutor", "Subscribe me to daily 6am nudges", "Show today's nudges"] },
  { title: "🌦 MORNING BRIEF", items: ["Give me my morning brief", "What's the weather?", "What's on my calendar?", "Show me today's news", "What's my P&L today?"] },
  { title: "🏠 HOME CONTROL", items: ["Turn on the bedroom TV", "Turn off the living room plug", "Register a new plug", "List my plugs"] },
  { title: "📷 EYES · VISION", items: ["Open EYES panel", "What do you see?", "Read this label", "What should I do next?", "Use webcam mode", "Use ESP32 cam"] },
  { title: "⏰ ALARMS & REMINDERS", items: ["Set alarm for 7am", "Daily alarm 7am", "Weekday alarm", "Weekend alarm", "List my alarms", "Delete the 7am alarm", "Daily exercise + water", "Weekday morning check"] },
  { title: "📊 SPEND & FINANCE", items: ["How much did I spend today?", "Log £5 on lunch", "Monthly report", "Open year heatmap", "Set my monthly budget"] },
  { title: "📱 SOCIAL & ADS", items: ["Post 'X' to Facebook and Instagram", "Ask Grok about today's headlines", "Show me my TikTok stats", "Search YouTube for 'X'"] },
  { title: "🖥 SCREENS & LAYOUT", items: ["Open the mirror screen", "Close the mirror", "Show me my markets widget", "Add a horoscope widget", "Set dark theme"] },
  { title: "📚 HISTORY & SESSIONS", items: ["List my sessions", "Open session from yesterday", "Recall our chat about the accountant", "Summarise this session"] },
  { title: "🔧 SETTINGS", items: ["Open settings", "Set my location to Sedbergh", "Change the voice", "List voices", "Show my command list", "What can you do?"] },
  { title: "🛠️ BUILD NEW FEATURES", items: ["Build me a TikTok scheduler", "Add a horoscope panel", "Integrate Notion"] },
];
